import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

import { ChatLoggingService } from './chat-logging.service';
import { ChatGetUsersDataHandler, ChatUserData, ChatUserDataItem } from '../interfaces/chat-user-data';

/**
 * Holds user data a host app supplies on top of Cloudlink (avatar today), mirroring lib-cl-chat's UserDataService:
 * the host registers a handler for on-demand fetches and pushes live changes through updateUserData().
 * ChatAdminService applies the result over its Cloudlink avatars; with no handler and no pushes nothing changes.
 */
@Injectable({
  providedIn: 'root'
})
export class ChatUserDataService {
  private getUsersDataHandler: ChatGetUsersDataHandler = null;
  private dataByUserId: { [userId: string]: ChatUserData } = {};
  // Requested (in flight or answered) so the many principal loads don't refetch the same user
  private requestedUserIds = new Set<string>();
  // Waiting for the next batch - or for a handler, when requested before the host registered one
  private pendingUserIds = new Set<string>();
  private flushTimer = null;
  // Each emission carries the user's full current data, not a delta - so {} after clearUserData() means no host
  // data (show Cloudlink's), unlike a null field, which means removed on the host
  public dataChange$: Subject<ChatUserDataItem> = new Subject<ChatUserDataItem>();

  constructor(private chatLoggingService: ChatLoggingService) {}

  public registerGetUsersDataHandler(handler: ChatGetUsersDataHandler) {
    this.getUsersDataHandler = typeof handler === 'function' ? handler : null;
    this.chatLoggingService.info({
      logMessage: 'chat-user-data.service.registerGetUsersDataHandler()',
      logParameters: [!!this.getUsersDataHandler]
    });
    this.scheduleFlush();
  }

  // A copy, so callers can't change the stored data behind dataChange$
  public getData(userId: string): ChatUserData | undefined {
    const data = this.dataByUserId[userId];
    return data && { ...data };
  }

  public updateUserData(userId: string, data: ChatUserData) {
    if (!userId || !data) {
      return;
    }
    // Per field: null clears it, undefined leaves it alone - so a status-only change keeps the avatar
    const current = this.dataByUserId[userId];
    const changes = Object.keys(data).filter(
      key => data[key] !== undefined && current?.[key] !== data[key]
    ) as (keyof ChatUserData)[];
    if (!changes.length) {
      return;
    }
    // Field names only - integrations carry access tokens
    this.chatLoggingService.debug({
      logMessage: 'chat-user-data.service.updateUserData()',
      logParameters: [userId, changes]
    });
    const next: ChatUserData = { ...current };
    changes.forEach(key => ((next as Record<string, unknown>)[key] = data[key]));
    this.dataByUserId[userId] = next;
    this.dataChange$.next({ userId, data: { ...next } });
  }

  // Drops a user's host data so the Cloudlink data shows again (lib-cl-chat does the same on unsubscribe), and
  // lets a later principal load ask for it anew
  public clearUserData(userId: string) {
    this.requestedUserIds.delete(userId);
    this.pendingUserIds.delete(userId);
    if (!this.dataByUserId[userId]) {
      return;
    }
    delete this.dataByUserId[userId];
    this.dataChange$.next({ userId, data: {} });
  }

  /**
   * Batches user ids for the registered handler, skipping any already requested. Fire-and-forget: results land
   * through updateUserData() and a failure only means those users keep their Cloudlink data.
   */
  public requestUsersData(userIds: string[]) {
    (userIds || []).forEach(userId => {
      if (userId && !this.requestedUserIds.has(userId)) {
        this.requestedUserIds.add(userId);
        this.pendingUserIds.add(userId);
      }
    });
    this.scheduleFlush();
  }

  public async getUsersData(userIds: string[]): Promise<ChatUserDataItem[]> {
    if (!this.getUsersDataHandler) {
      throw new Error('Get users data handler is not registered.');
    }
    const requestedUserIds = [...new Set((userIds || []).filter(userId => !!userId))];
    if (!requestedUserIds.length) {
      return [];
    }
    requestedUserIds.forEach(userId => this.requestedUserIds.add(userId));
    const results = (await this.getUsersDataHandler(requestedUserIds)) || [];
    results.forEach(item => {
      // A user cleared while this was in flight (e.g. lib-cl-chat dropped them) left requestedUserIds - their answer is stale
      if (item?.userId && item?.data && this.requestedUserIds.has(item.userId)) {
        this.updateUserData(item.userId, item.data);
      }
    });
    return results;
  }

  private scheduleFlush() {
    if (!this.getUsersDataHandler || !this.pendingUserIds.size || this.flushTimer) {
      return;
    }
    // Defer a tick so a getPrincipals() chunk lands in one handler call instead of one per user
    this.flushTimer = setTimeout(() => this.flush(), 0);
  }

  private async flush() {
    this.flushTimer = null;
    // Handler removed since this was scheduled - keep the ids pending for the next one registered
    if (!this.getUsersDataHandler) {
      return;
    }
    const userIds = [...this.pendingUserIds];
    this.pendingUserIds.clear();
    try {
      await this.getUsersData(userIds);
    } catch (ex) {
      // Let a later principal load ask again
      userIds.forEach(userId => this.requestedUserIds.delete(userId));
      this.chatLoggingService.warn({
        logMessage: 'chat-user-data.service.flush(): Failed to get users data, keeping Cloudlink data',
        logParameters: [userIds.length, ex]
      });
    }
  }
}
